import { useEffect } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { ExtractRouteParams } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";

import { AlertError, ContentSingleColumn } from "~components";
import { AUTH_URLS, createSkillBuilderTaskURL } from "~pages";
import {
  computeGroupsState,
  configurationVersions,
  getConfiguration,
  Group,
  SkillBuilderConfiguration,
  type Version,
} from "~skillBuilder/utils";
import { GET, SkillBuilderAttribute } from "~utils";

import { TasksPageBase } from "./TasksPageBase";

type SkillBuilderTasksPageProps = RouteComponentProps<
  ExtractRouteParams<(typeof AUTH_URLS)["skillBuilderTasks"], string>
>;

/***** Component *****/
export function SkillBuilderTasksPage(props: SkillBuilderTasksPageProps) {
  const { history, match } = props;
  const { version } = match.params;

  // To capture group name in order to open corresponding accordion
  const historyState = history.location.state as { groupName?: Group["name"] };

  /***** Queries ******/
  const {
    data: subDistrictData,
    isSuccess: isSubDistrictDataSuccess,
    error: districtDataError,
  } = useQuery({
    queryKey: ["fetchSubDistricts"],
    queryFn: () => GET("/api/sub/skillbuilder-districts"),
    /**
     * Data is considered stale after 1 hour
     * Data stays in cache for 2 hours
     * Users see immediate data on mount (even if stale)
     * Fresh data is fetched in the background and cache updated
     * UI automatically updates when fresh data arrives
     * Memory is eventually cleaned up after 2 hours of inactivity
     */
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });

  // Determine what district configuration(s) are available to load for this sub
  const districtIds = subDistrictData?.data.associatedDistrictIds;
  const districtConfigurations = districtIds?.filter(
    (districtId): districtId is Version => districtId in configurationVersions,
  );

  const baseQueries = [
    {
      queryKey: ["skillBuilderConfiguration", version],
      queryFn: () => getConfiguration(version as Version),
      // Cache the configuration for the lifetime of the application since this
      // only changes on new deployments which will require an application refresh.
      // This will avoid all future calls using this key from making a network request.
      staleTime: Infinity,
    },
    {
      queryKey: ["skillBuilderAttributes"],
      queryFn: () => GET("/api/sub/skillbuilder"),
      // Since the response is `{ data: [...subAttribute] }` we can clean up the response
      select: (data: unknown) =>
        // @ts-expect-error - fix types
        data?.data.map((attribute) => ({
          ...attribute,
        })) as Array<SkillBuilderAttribute>,
    },
  ];

  const districtQueries =
    districtConfigurations && districtConfigurations.length > 0
      ? districtConfigurations?.map((districtId) => ({
          queryKey: ["skillBuilderConfiguration", districtId],
          queryFn: () => getConfiguration(districtId),
          staleTime: Infinity,
          enabled: isSubDistrictDataSuccess,
        }))
      : null;

  let queries = [];
  if (!districtQueries) {
    queries = [...baseQueries];
  } else {
    queries = [...baseQueries, ...districtQueries];
  }

  const { data: groupsComputed, errors } = useQueries({
    queries,
    // Note: using `combine` results in not being able to access other query properties, such as error
    // https://tanstack.com/query/v5/docs/framework/react/reference/useQueries#combine
    // therefore specifically returning these to be able to update UI accordingly
    combine: (results) => {
      const versionConfiguration = results[0].data as SkillBuilderConfiguration | undefined;
      const skillBuilderAttributes = results[1].data as Array<SkillBuilderAttribute> | undefined;
      const districtConfigurationResults = results.slice(2);
      const districtConfigurations = districtConfigurationResults
        .map((config) => config.data as SkillBuilderConfiguration | undefined)
        .filter((config): config is SkillBuilderConfiguration => config !== undefined)
        .flatMap((config) => config.groups);

      if (!versionConfiguration || !skillBuilderAttributes) {
        return {
          data: undefined,
          // `filter(Boolean)` to remove `null` when error does not exist
          errors: results.map((res) => res.error).filter(Boolean),
        };
      }

      // merge any district configurations with the swing configuration
      const configuration: SkillBuilderConfiguration = {
        groups: [...versionConfiguration.groups, ...districtConfigurations],
        version: versionConfiguration.version,
      };

      return {
        data: computeGroupsState({
          groups: removeDuplicateGroups(configuration.groups),
          syncedSkillBuilderAttributes: skillBuilderAttributes,
        }),
      };
    },
  });

  const isErrors = (errors && errors.length > 0) || districtDataError;

  useEffect(() => {
    // only run on mount to clear history state, which closes all accordions on refresh
    if (historyState?.groupName) delete historyState.groupName;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /***** Handlers *****/

  // Routes to the Task page
  function handleTaskClick(taskIndex: number) {
    history.push(createSkillBuilderTaskURL(version, taskIndex));
  }

  /***** Render *****/

  return (
    <ScorePage title="Skill Builder" hasBack isLoading={!groupsComputed && !isErrors}>
      <ContentSingleColumn>
        {isErrors && <AlertError />}
        {!isErrors && groupsComputed && (
          <TasksPageBase
            currentGroupName={historyState?.groupName}
            groupsComputed={groupsComputed}
            onTaskClick={handleTaskClick}
          />
        )}
      </ContentSingleColumn>
    </ScorePage>
  );
}

/**
 * Removes duplicate groups from the list of groups.
 * If a group is custom, it will be kept.
 * If a group is not custom, it will be removed if it has the same name as a custom group.
 *
 * @param groups - The list of groups to remove duplicates from.
 * @returns The list of groups with duplicates removed.
 */
function removeDuplicateGroups(groups: Group[]) {
  const customGroups = groups.filter((group) => group.isCustom);
  const lookup = Map.groupBy(customGroups, (g) => g.name);

  return groups.filter((g) => !(!g.isCustom && lookup.has(g.name)));
}
